import { CheckCircleIcon, InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Link,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { Badge, Tooltip } from "components";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { QuantumDemandContact } from "./QuantumDemandContactCard.types";

const TOPICS_RESEARCHED_TOOLTIP =
  "Persona-level topic research from the Intentsify Signal Graph.";

interface SectionLabelProps {
  children: ReactNode;
  tooltip?: string;
}

const SectionLabel = ({ children, tooltip }: SectionLabelProps) => (
  <Flex alignItems="center" gap={2} mb={2}>
    <Text
      textTransform="uppercase"
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="wider"
      color="gray.500"
    >
      {children}
    </Text>
    {tooltip && (
      <Tooltip label={tooltip} shouldWrapChildren>
        <InfoIcon boxSize={3} color="gray.400" />
      </Tooltip>
    )}
  </Flex>
);

interface ContactInfoRowProps {
  label: string;
  children: ReactNode;
}

const ContactInfoRow = ({ label, children }: ContactInfoRowProps) => {
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Flex gap={4}>
      <Text color={mutedColor} minW="60px">
        {label}
      </Text>
      <Text>{children}</Text>
    </Flex>
  );
};

type EngagementRow = {
  type: string;
  colorScheme: string;
  label: string;
  date?: string;
};

interface QuantumDemandContactCardProps {
  contact: QuantumDemandContact;
}

export const QuantumDemandContactCard = ({
  contact,
}: QuantumDemandContactCardProps) => {
  const { isOpen, onToggle } = useDisclosure();

  const borderColor = useColorModeValue("gray.200", "gray.600");
  const dividerColor = useColorModeValue("gray.200", "gray.600");
  const background = useColorModeValue("white", "gray.700");
  const topicBackground = useColorModeValue("gray.100", "gray.600");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const {
    firstName,
    lastName,
    jobTitle,
    hasOptedIn,
    email,
    telephone,
    linkedInProfileUrl,
    contactEngagementSummary,
    engagement,
    topicsResearched,
  } = contact;

  const engagementRows: EngagementRow[] = [
    ...engagement.pageVisits.map((visit) => ({
      type: "Page Visit",
      colorScheme: "green",
      label: visit.url,
      date: visit.date,
    })),
    ...engagement.emailOpens.map((emailOpen) => ({
      type: "Email Opened",
      colorScheme: "cyan",
      label: "",
      date: emailOpen.date,
    })),
    ...engagement.adClicks.map((adClick) => ({
      type: "Ad Click",
      colorScheme: "blue",
      label: "",
      date: adClick.date,
    })),
    ...engagement.contentDownloads.map((download) => ({
      type: "Content Downloaded",
      colorScheme: "teal",
      label: download.asset,
      date: download.date,
    })),
  ];

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      background={background}
      px={6}
      py={5}
      w={560}
    >
      <Flex alignItems="center" gap={3}>
        <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
          {firstName} {lastName}
        </Text>
        {hasOptedIn && (
          <Tooltip label="Opted-in contact" shouldWrapChildren>
            <CheckCircleIcon boxSize={5} color="teal.500" />
          </Tooltip>
        )}
      </Flex>

      <Text fontSize="lg" color={mutedColor} mt={1}>
        {jobTitle}
      </Text>

      <Button
        variant="link"
        colorScheme="blue"
        fontWeight="normal"
        mt={2}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? "Hide contact information" : "Show contact information"}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <Flex flexDirection="column" gap={1} mt={3}>
          <ContactInfoRow label="Email">
            <Link href={`mailto:${email}`}>{email}</Link>
          </ContactInfoRow>
          {telephone && (
            <ContactInfoRow label="Phone">
              <Link href={`tel:${telephone}`}>{telephone}</Link>
            </ContactInfoRow>
          )}
          {linkedInProfileUrl && (
            <Link
              href={
                linkedInProfileUrl.includes("http")
                  ? linkedInProfileUrl
                  : `https://${linkedInProfileUrl}`
              }
              isExternal
              color="blue.500"
            >
              View LinkedIn Profile →
            </Link>
          )}
        </Flex>
      </Collapse>

      {topicsResearched.length > 0 && (
        <Box mt={5}>
          <SectionLabel tooltip={TOPICS_RESEARCHED_TOOLTIP}>
            Topics Researched
          </SectionLabel>
          <Flex flexWrap="wrap" gap={2}>
            {topicsResearched.map((topic) => (
              <Box
                key={topic}
                background={topicBackground}
                borderRadius="md"
                fontSize="sm"
                px={3}
                py={1.5}
              >
                {topic}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {engagementRows.length > 0 && (
        <Box mt={5}>
          <SectionLabel>Engagements</SectionLabel>
          {engagementRows.map((row, index) => (
            <Flex
              key={`${row.type}-${row.label}-${index}`}
              alignItems="center"
              gap={4}
              py={2}
              borderBottom={
                index === engagementRows.length - 1 ? undefined : "1px dashed"
              }
              borderColor={dividerColor}
            >
              <Badge
                colorScheme={row.colorScheme}
                borderRadius="md"
                textTransform="none"
                fontSize="sm"
                fontWeight="semibold"
                minW="120px"
                textAlign="center"
                px={2}
                py={1}
              >
                {row.type}
              </Badge>
              <Tooltip label={row.label} isDisabled={!row.label}>
                <Text flex={1} fontSize="sm" noOfLines={1}>
                  {row.label}
                </Text>
              </Tooltip>
              {row.date && (
                <Text fontSize="sm" color={mutedColor} whiteSpace="nowrap">
                  {DateTime.fromISO(row.date).toFormat("LLL d")}
                </Text>
              )}
            </Flex>
          ))}
        </Box>
      )}

      {contactEngagementSummary && (
        <Box mt={5}>
          <SectionLabel>Next Best Action</SectionLabel>
          <Text>{contactEngagementSummary}</Text>
        </Box>
      )}
    </Box>
  );
};
